/**
 * How each kind of notification reaches somebody, and changing it.
 *
 * A file of its own rather than two more exports in `notifications.ts`, which
 * mirrors `packages/shared/src/api` holding both in one: the wire shapes are one
 * resource because they are one route prefix, and these are two calls a
 * different screen makes. The barrel in `lib/api.ts` is what makes that
 * invisible to callers.
 */
import type {
  NotificationDigestResponse,
  NotificationPreference,
  NotificationPreferencesResponse,
  SetNotificationDigestRequest,
  SetNotificationPreferenceRequest,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/**
 * Everything the reader may choose about, with the effective delivery of each.
 *
 * `principalOndoId` is WHOSE, and it is the same parameter the inbox takes for
 * the same reason — notifications are addressed to firms as well as people, so
 * the screen that quiets an inbox has to reach every inbox the screen that reads
 * one does. Absent is the reader's own.
 */
export const fetchNotificationPreferences = (principalOndoId?: string | null) =>
  request<NotificationPreferencesResponse>(
    `/notifications/preferences${inboxQuery(principalOndoId)}`,
  );

/**
 * Set how one kind arrives. Answers with what was written, not what was asked.
 *
 * One kind per call, which is the route's own decision: the screen saves on
 * change, and a request carrying every row would silently rewrite choices made
 * on another device since it loaded.
 */
export const setNotificationPreference = (
  body: SetNotificationPreferenceRequest,
  principalOndoId?: string | null,
) =>
  requestWith<NotificationPreference>(
    "PUT",
    `/notifications/preferences${inboxQuery(principalOndoId)}`,
    body,
  );

/**
 * WHEN the daily digest arrives, and whether this principal chose it — OND-96.
 *
 * A call of its own rather than a field on the preferences response, and the
 * split is the route's: a preference is per KIND and this is per PRINCIPAL.
 */
export const fetchNotificationDigest = (principalOndoId?: string | null) =>
  request<NotificationDigestResponse>(
    `/notifications/digest${inboxQuery(principalOndoId)}`,
  );

/**
 * Move it. An hour 0-23, IN UTC — the owner's answer was "one global clock, not
 * 09:00 in each reader's local timezone", and the field name is what keeps that
 * from being quietly read as local by a client.
 */
export const setNotificationDigestHour = (
  body: SetNotificationDigestRequest,
  principalOndoId?: string | null,
) =>
  requestWith<NotificationDigestResponse>(
    "PUT",
    `/notifications/digest${inboxQuery(principalOndoId)}`,
    body,
  );

function inboxQuery(principalOndoId?: string | null): string {
  return principalOndoId ? `?principal=${encodeURIComponent(principalOndoId)}` : "";
}
